import { cn } from "./classNames-DcGnTh2N.js";
import { ChevronDown, Plus, X } from "./icons-ZIeZXXS7.js";
import { Button } from "./Button-BNkyYU8u.js";
import { Popover } from "./Popover-DnBw5Uay.js";
import { Badge } from "./Badge-CqlOE5A2.js";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/blocks/PageToolbar/PageToolbarToken.tsx
/**
* Editable filter token (Query Builder variant) — `Badge` pill with a `chevron-down`
* popover trigger for editing values and an `×` dismiss button. The popover content
* is supplied by the caller via the `popover` slot (typically a `Combobox` or option list).
*/
const PageToolbarToken = ({ children, popover, onRemove, removeAriaLabel, ...props }) => /* @__PURE__ */ jsxs(Popover, { children: [/* @__PURE__ */ jsx(Badge, {
	...props,
	asChild: true,
	variant: "pill",
	color: "blue-inverse",
	suffix: /* @__PURE__ */ jsx(Button, {
		type: "button",
		variant: "inline",
		size: "icon-2xs",
		"aria-label": removeAriaLabel,
		onClick: onRemove,
		children: /* @__PURE__ */ jsx(X, { className: "tw:size-3" })
	}),
	children: /* @__PURE__ */ jsxs(Popover.Trigger, {
		type: "button",
		className: "tw:inline-flex tw:cursor-pointer tw:items-center tw:gap-1 tw:bg-inherit tw:text-inherit tailwindcss-preflight",
		children: [children, /* @__PURE__ */ jsx(ChevronDown, { className: "tw:size-3" })]
	})
}), /* @__PURE__ */ jsx(Popover.Content, {
	align: "start",
	children: popover
})] });
//#endregion
//#region src/blocks/PageToolbar/PageToolbar.tsx
const Root = ({ ref, className, children, ...props }) => /* @__PURE__ */ jsx("div", {
	ref,
	className: cn("tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:w-full", className),
	...props,
	children
});
const Search = ({ ref, className, children, ...props }) => /* @__PURE__ */ jsx("div", {
	ref,
	className: cn("tw:mr-auto tw:flex tw:items-center tw:gap-2 tw:w-full tw:sm:w-auto tw:sm:min-w-64 tw:sm:max-w-md", "tw:*:first:min-w-0 tw:*:first:flex-1", className),
	...props,
	children
});
const Right = ({ ref, className, children, ...props }) => /* @__PURE__ */ jsx("div", {
	ref,
	className: cn("tw:flex tw:flex-wrap tw:items-center tw:gap-2", className),
	...props,
	children
});
const ControlGroup = ({ ref, className, children, ...props }) => /* @__PURE__ */ jsx("div", {
	ref,
	className: cn("tw:flex tw:items-center tw:gap-2", className),
	...props,
	children
});
const ActiveFilters = ({ ref, className, children, ...props }) => /* @__PURE__ */ jsx("div", {
	ref,
	className: cn("tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:basis-full", className),
	...props,
	children
});
const Chip = ({ children, onRemove, removeAriaLabel, ...props }) => /* @__PURE__ */ jsx(Badge, {
	...props,
	variant: "pill",
	color: "blue-inverse",
	suffix: /* @__PURE__ */ jsx(Button, {
		type: "button",
		variant: "inline",
		size: "icon-2xs",
		"aria-label": removeAriaLabel,
		onClick: onRemove,
		children: /* @__PURE__ */ jsx(X, { className: "tw:size-3" })
	}),
	children
});
const AddFilter = ({ className, children, ...props }) => /* @__PURE__ */ jsxs(Button, {
	variant: "invisible",
	size: "small",
	className: cn("tw:gap-1.5", className),
	...props,
	children: [/* @__PURE__ */ jsx(Plus, { className: "tw:size-4" }), children ?? "Add filter"]
});
const Clear = ({ className, children, ...props }) => /* @__PURE__ */ jsx(Button, {
	variant: "invisible",
	size: "small",
	className: cn("tw:ml-auto", className),
	...props,
	children: children ?? "Clear all"
});
/**
* Search / filter toolbar for index pages, between `PageLayout.Header` and `PageLayout.Content`.
*
* - Right cluster order: `Filters` → `Sort` → `Actions` → `View`; nothing after `View`.
* - `ActiveFilters` only while a filter is active; chips or tokens, never both in one toolbar.
*
* @see https://github.com/evviku/design-system-docs/blob/main/docs/patterns/toolbar/toolbar.md
*/
const PageToolbar = Object.assign(Root, {
	Search,
	Right,
	Filters: ControlGroup,
	Sort: ControlGroup,
	Actions: ControlGroup,
	/** Always last in the right cluster. */
	View: ControlGroup,
	/** Render only while a filter is active. */
	ActiveFilters,
	/** Non-editable filter chip. */
	Chip,
	/** Editable filter token; the caller supplies the edit popover via `popover`. */
	Token: PageToolbarToken,
	AddFilter,
	Clear
});
//#endregion
export { PageToolbar };

//# sourceMappingURL=PageToolbar-LKP6PiP_.js.map